import React, { useCallback, useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Chart,
  ChartCategoryAxis,
  ChartCategoryAxisItem,
  ChartLegend,
  ChartPanes,
  ChartPane,
  ChartValueAxis,
  ChartValueAxisItem,
  ChartSeries,
  ChartSeriesItem,
  ChartTitle,
  ChartTooltip,
} from '@progress/kendo-react-charts';
import 'hammerjs';
import Grid from '@material-ui/core/Grid';
import PageTitle from 'components/PageTitle';
import SearchCriteria from 'components/SearchCriteria';
import ReportContent from 'components/ReportContent';
import { fetchResource } from 'helpers/fetch';
import { API_CONSTANTS } from 'constants/api';
import DataGrid from 'components/DataGrid';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    marginTop: '15px',
  },

  footnote: {
    marginTop: '20px',
    marginRight: '0px',
    marginBottom: '20px',
    marginLeft: '0px',
    fontSize: '0.85em',
    color: '#333333',
    padding: '10px',
  },
}));

export const getTransactionTrends = async (criteria) => {
  const data = await fetchResource(API_CONSTANTS.REPORT_TRANSACTION_TRENDS, {
    method: 'POST',
    data: criteria,
  });
  return data;
};

const TransactionTypeComparisonPage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const classes = useStyles();
  const defaultStep = Math.floor(data.length / 10);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  const transactionTypeHeader = [
    {
      dataColumn: 'yearQuarter',
      label: 'Year - Quarter',
      numeric: false,
      sortColumn: 'yearQuarter',
    },
    {
      dataColumn: 'showNewBusiness',
      label: 'New Business',
      numeric: true,
      sortColumn: 'newBusiness',
    },
    {
      dataColumn: 'showCancelledBusiness',
      label: 'Cancelled Business',
      numeric: true,
      sortColumn: 'cancelledBusiness',
    },
    {
      dataColumn: 'showRenewedBusiness',
      label: 'Renewed Business',
      numeric: true,
      sortColumn: 'renewedBusiness',
    },
  ];

  const [min, setMin] = React.useState(0);
  const [max, setMax] = React.useState(data.length);
  const [step, setStep] = React.useState({
    step: 0,
  });

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  function handleResize() {
    setScreenWidth(window.innerWidth);
  }

  const defaultTooltipRender = ({ point }) => {
    return (
      <div style={{ padding: '0.25rem' }}>
        <div>Quarter: {data.transactionTrends[min + point.categoryIndex].yearQuarter}</div>
        <div>
          Premium:{' '}
          {point.series.category === 'New Business'
            ? data.transactionTrends[min + point.categoryIndex].showNewBusiness
            : point.series.category === 'Cancelled Business'
            ? data.transactionTrends[min + point.categoryIndex].showCancelledBusiness
            : point.series.category === 'Renewed Business'
            ? data.transactionTrends[min + point.categoryIndex].showRenewedBusiness
            : null}
        </div>
      </div>
    );
  };

  const onSelectEnd = (args) => {
    setMin(args.from);
    setMax(args.to);
    setStep({
      step: Math.floor((args.to - args.from) / 10),
    });
  };

  const handleSearch = useCallback(async (criteria) => {
    setLoading(true);
    // eslint-disable-next-line no-unused-vars
    const response = await getTransactionTrends(criteria);
    setData(response);
    setMax(response.transactionTrends.length);
    setMin(response.transactionTrends.length > 18 ? response.transactionTrends.length - 18 : 1);
    setLoading(false);
  }, []);

  return (
    <>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <PageTitle>Transaction Type Comparison</PageTitle>
          <SearchCriteria
            onSearch={handleSearch}
            disableSearch={loading}
            hideEndDate={false}
            hideCoverageCodes={false}
            hideInsurers={false}
            hideCounties={false}
            allowMultipleCounty={true}
            allowMultipleInsurer={true}
          />
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {data.transactionTrends ? (
              data.transactionTrends.length > 0 ? (
                <ReportContent loading={loading} hasData={data.transactionTrends.length > 0}>
                  <Chart
                    style={{
                      height: 400,
                    }}
                    onSelectEnd={onSelectEnd}
                  >
                    <ChartTitle text="Transaction Type Comparison Chart" />
                    <ChartLegend
                      position={screenWidth < 1024 ? 'top' : 'right'}
                      orientation={screenWidth < 1024 ? 'horizontal' : 'vertical'}
                    />
                    <ChartTooltip render={defaultTooltipRender} />
                    <ChartCategoryAxis>
                      <ChartCategoryAxisItem
                        categories={data.transactionTrends
                          .map((i) => i.yearQuarter)
                          .slice(min, max)}
                        startAngle={45}
                        categoryField="yearQuarter"
                        min={min}
                        max={max}
                        majorGridLines={step}
                        majorTicks={step}
                        labels={{
                          step: max - min <= 18 ? 1 : 4, // Top Y-labels
                        }}
                      />
                      <ChartCategoryAxisItem
                        data={data.navigatorData}
                        categoryField="xAxis"
                        name="navigatorAxis"
                        labels={{
                          step: 4, // Bottom Y-labels
                        }}
                        majorGridLines={{
                          step: defaultStep,
                        }}
                        majorTicks={{
                          step: defaultStep,
                        }}
                        pane="navigator"
                        select={{
                          from: min,
                          to: max,
                        }}
                      />
                    </ChartCategoryAxis>
                    <ChartPanes>
                      <ChartPane />
                      <ChartPane name="navigator" height={100} />
                    </ChartPanes>
                    <ChartValueAxis>
                      <ChartValueAxisItem
                        labels={{
                          format: '$#,###.##',
                        }}
                      />
                      <ChartValueAxisItem
                        labels={{
                          visible: false,
                          format: '$#,###.##',
                          step: 4,
                        }}
                        name="valueNavigatorAxis"
                        pane="navigator"
                      />
                    </ChartValueAxis>
                    <ChartSeries>
                      {data.lineData.map((item, idx) => (
                        <ChartSeriesItem
                          key={idx}
                          type="line"
                          data={item.data}
                          name={item.name}
                          category={item.name}
                        />
                      ))}
                      {data.navigatorData ? (
                        data.navigatorData.length > 0 ? (
                          <ChartSeriesItem
                            type="column"
                            color={'#37609d'}
                            data={data.navigatorData}
                            field="yAxis"
                            axis="valueNavigatorAxis"
                            categoryAxis="navigatorAxis"
                            categoryField="xAxis"
                          />
                        ) : null
                      ) : null}
                    </ChartSeries>
                  </Chart>
                </ReportContent>
              ) : null
            ) : null}
          </Grid>
          <Grid item xs={12}>
            {data.transactionTrends ? (
              data.transactionTrends.length > 0 ? (
                <>
                  <DataGrid
                    data={data.transactionTrends}
                    headers={transactionTypeHeader}
                    defaultOrderBy="yearQuarter"
                    defaultOrder="asc"
                  ></DataGrid>
                </>
              ) : null
            ) : null}
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <div className={classes.footnote}>
            <strong>Note:</strong> The information included in this report is inclusive of
            single-state Mississippi policy filings only as reported to the Mississippi Surplus
            Lines Association. For the Insurers By Premium By Coverage Report, "Policy Count" refers
            to the number of policies in which the insurer is associated. For a count of policies
            written in the market, please refer to the Fast Facts Report.
          </div>
        </Grid>
      </Grid>
    </>
  );
};

export default TransactionTypeComparisonPage;
